<template>
  <rmr-table-header
    heading-class="gw-toolbar__heading"
    controls-class="gw-toolbar__controls"
  >
    <template #heading>
      <div class="rmr-table-shell__eyebrow">Résultats</div>
      <div class="rmr-table-shell__title">
        {{ isVideo ? "Vidéos les plus récentes" : "Sources les plus pertinentes" }}
      </div>
      <!-- Own line: this is the only variable-length text here, and inline it
           pushed the controls until the view toggle wrapped under them. -->
      <div v-if="countLabel" class="gw-toolbar__count">{{ countLabel }}</div>
    </template>

    <template #controls>
      <div class="rmr-table-shell__filter-item gw-toolbar__search">
        <label class="rmr-table-shell__filter-label">Recherche</label>
        <rmr-text-field
          :value="search"
          placeholder="Rechercher dans les titres…"
          :prepend-inner-icon="icons.mdiMagnify"
          hide-details
          dense
          clearable
          @input="$emit('update:search', $event || '')"
        />
      </div>

      <!-- Pas de tri en vidéo : une source vidéo n'a pas de score, le serveur
           trie par date de publication et rien d'autre. -->
      <div
        v-if="!isVideo"
        class="rmr-table-shell__filter-item gw-toolbar__control"
      >
        <label class="rmr-table-shell__filter-label">Tri</label>
        <rmr-segmented-control
          :value="sortBy"
          :items="sortItems"
          @input="$emit('update:sortBy', $event)"
        />
      </div>

      <div class="rmr-table-shell__filter-item gw-toolbar__control">
        <label class="rmr-table-shell__filter-label">Vue</label>
        <rmr-segmented-control
          :value="view"
          :items="viewItems"
          @input="$emit('update:view', $event)"
        />
      </div>

      <label class="gw-toolbar__toggle">
        <input
          type="checkbox"
          class="gw-toolbar__checkbox"
          :checked="hideTreated"
          @change="$emit('update:hideTreated', $event.target.checked)"
        />
        Masquer les déjà traités
      </label>
    </template>
  </rmr-table-header>
</template>

<script>
import { computed } from "@vue/composition-api";
import {
  mdiFormatListBulleted,
  mdiMagnify,
  mdiViewGridOutline,
} from "@mdi/js";

import RmrSegmentedControl from "@/components/ui/actions/RmrSegmentedControl.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";
import RmrTextField from "@/components/ui/forms/RmrTextField.vue";

export default {
  name: "SourcesResultsToolbar",
  components: {
    RmrSegmentedControl,
    RmrTableHeader,
    RmrTextField,
  },
  props: {
    search: { type: String, default: "" },
    sortBy: { type: String, default: "date" },
    view: { type: String, default: "table" },
    hideTreated: { type: Boolean, default: true },
    shownCount: { type: Number, default: 0 },
    total: { type: Number, default: 0 },
    isVideo: { type: Boolean, default: false },
  },
  setup(props) {
    const countLabel = computed(() => {
      if (props.total === 0) return "";
      return `${props.shownCount} affichées sur ${props.total}`;
    });

    return {
      countLabel,
      icons: { mdiMagnify },
      sortItems: [
        { value: "date", label: "Date" },
        { value: "score", label: "Score" },
      ],
      viewItems: [
        { value: "table", label: "Table", icon: mdiFormatListBulleted },
        { value: "cards", label: "Cartes", icon: mdiViewGridOutline },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../_global-watch.scss";

// The controls row is `align-items: flex-end`. Giving every control the same
// box height makes their contents line up, instead of each one landing on its
// own baseline — the checkbox has no label above it and sat lower than the rest.
.gw-toolbar__search ::v-deep .v-input__slot {
  min-height: $gw-control-height !important;
  margin-bottom: 0 !important;
}

.rmr-segmented {
  min-height: $gw-control-height;
}

// The shared shell sets `flex: 0 0 auto` on the heading and `nowrap` on the
// controls, so nothing could give way and the view toggle slid under the
// "Masquer les déjà traités" checkbox. ::v-deep is required: these classes sit
// on RmrTableHeader's inner elements, outside this component's scope.
::v-deep .gw-toolbar__heading {
  flex: 0 1 auto;
  min-width: 0;
  align-self: flex-start;
}

::v-deep .gw-toolbar__controls {
  flex-wrap: wrap;
  row-gap: 10px;
}

.gw-toolbar__count {
  margin-top: 4px;
  font-size: 12px;
  color: $rmr-text-subtle;
}

.gw-toolbar__search {
  width: 240px;
  max-width: 240px;
  flex: 1 1 180px;
}

.gw-toolbar__control {
  min-width: 0;
}

.gw-toolbar__toggle {
  display: flex;
  align-items: center;
  gap: 7px;
  min-height: $gw-control-height;
  font-size: 12.5px;
  color: $rmr-text-muted;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.gw-toolbar__checkbox {
  width: 15px;
  height: 15px;
  accent-color: $rmr-indigo;
  cursor: pointer;
}
</style>
